import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useOrder } from '../context/OrderContext';
import { QrCode, ShieldCheck, CheckCircle2, ChevronRight, MessageCircle, Hash } from 'lucide-react';

const Payment = () => {
  const { cartTotal, clearCart } = useCart();
  const { orderDetails, completeOrder } = useOrder();
  const navigate = useNavigate();

  const [transactionRef, setTransactionRef] = useState('');
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [finalOrder, setFinalOrder] = useState(null);

  if (!orderDetails) {
    navigate('/cart');
    return null;
  }

  const handleConfirm = () => {
    if (!transactionRef.trim()) {
      setError('Transaction/Reference Number is required');
      return;
    }
    if (transactionRef.length < 5) {
      setError('Please enter a valid reference number (min 5 characters)');
      return;
    }

    const savedOrder = completeOrder(transactionRef);
    setFinalOrder(savedOrder);
    setIsSuccess(true);
    clearCart();

    // 1. Prepare the data for the Google Sheet
    const orderDataForSheet = {
      date: new Date().toLocaleString(),
      orderId: savedOrder.orderId,
      customerName: savedOrder.customer.fullName,
      customerMobile: savedOrder.customer.mobile,
      customerAddress: savedOrder.customer.address,
      items: savedOrder.items.map(i => `${i.name} (x${i.quantity})`).join(', '),
      totalAmount: savedOrder.totalAmount,
      transactionRef: transactionRef
    };

    const GOOGLE_SCRIPT_URL = process.env.REACT_APP_GOOGLE_SCRIPT_URL;

    fetch(GOOGLE_SCRIPT_URL, {
      method: 'POST',
      body: JSON.stringify(orderDataForSheet),
      headers: {
        'Content-Type': 'text/plain;charset=utf-8',
      }
    }).catch(error => console.error('Error saving to sheet:', error));

    const wText =
      `*New Order Confirmed!*%0A` +
      `*Order ID:* ${savedOrder.orderId}%0A` +
      `*Name:* ${savedOrder.customer.fullName}%0A` +
      `*Mobile:* ${savedOrder.customer.mobile}%0A` +
      `*Address:* ${savedOrder.customer.address}%0A%0A` +
      `*Items:*%0A${savedOrder.items.map(i => `- ${i.name} (x${i.quantity})`).join('%0A')}%0A%0A` +
      `*Total Paid:* ₹${savedOrder.totalAmount.toFixed(2)}`;

    const wUrl = `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${wText}`;
    window.open(wUrl, '_blank');
  };

  const upiId = process.env.REACT_APP_UPI_ID;
  const payeeName = process.env.REACT_APP_PAYEE_NAME;
  const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(payeeName)}&am=${cartTotal.toFixed(2)}&cu=INR&tn=HeartyCrafts Order`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(upiUrl)}&color=000000&bgcolor=ffffff`;

  if (isSuccess && finalOrder) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap');

          .pay-success-wrapper {
            font-family: 'Sora', sans-serif;
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            box-sizing: border-box;
          }

          .pay-success-card {
            width: 100%;
            max-width: 480px;
            background: var(--glass-panel, rgba(255,255,255,0.08));
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255,255,255,0.15);
            border-radius: 28px;
            padding: 3rem 2.5rem;
            text-align: center;
            box-shadow: 0 32px 80px rgba(0,0,0,0.25);
            animation: slideUp 0.55s cubic-bezier(0.22,1,0.36,1) both;
          }

          @keyframes slideUp {
            from { opacity: 0; transform: translateY(40px) scale(0.97); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }

          .pay-success-icon {
            width: 88px;
            height: 88px;
            border-radius: 50%;
            background: rgba(16,185,129,0.12);
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1.75rem;
            box-shadow: 0 0 0 12px rgba(16,185,129,0.06);
            animation: popIn 0.5s 0.2s cubic-bezier(0.34,1.56,0.64,1) both;
          }

          @keyframes popIn {
            from { transform: scale(0); opacity: 0; }
            to   { transform: scale(1); opacity: 1; }
          }

          .pay-success-title {
            font-size: 2.2rem;
            font-weight: 800;
            color: #10b981;
            margin: 0 0 0.8rem;
            letter-spacing: -1px;
          }

          .pay-success-name {
            font-size: 1.2rem;
            font-weight: 600;
            color: var(--text-main);
            margin: 0 0 0.4rem;
          }

          .pay-success-sub {
            font-size: 0.95rem;
            color: var(--text-muted);
            margin: 0 0 2.5rem;
            line-height: 1.6;
          }

          .pay-whatsapp-badge {
            display: inline-flex;
            align-items: center;
            gap: 0.55rem;
            background: rgba(37,211,102,0.12);
            color: #25D366;
            border: 1px solid rgba(37,211,102,0.25);
            padding: 0.65rem 1.4rem;
            border-radius: 50px;
            font-size: 0.88rem;
            font-weight: 600;
            margin-bottom: 2rem;
            width: 100%;
            justify-content: center;
            box-sizing: border-box;
          }

          .pay-order-summary {
            background: rgba(255,255,255,0.05);
            border: 1px dashed rgba(255,255,255,0.18);
            border-radius: 16px;
            padding: 1.5rem 1.75rem;
            text-align: left;
            margin-bottom: 2.25rem;
          }

          .pay-order-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 0.92rem;
            margin-bottom: 0.6rem;
          }

          .pay-order-row:last-child { margin-bottom: 0; }

          .pay-order-label {
            color: var(--text-muted, #94a3b8);
          }

          .pay-order-value {
            font-weight: 700;
            color: var(--text-main, #f1f5f9);
          }

          .pay-order-amount {
            font-size: 1.2rem;
            font-weight: 800;
            background: linear-gradient(135deg, #60a5fa, #a78bfa);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .pay-btn-return {
            width: 100%;
            padding: 1rem;
            border-radius: 14px;
            border: none;
            background: linear-gradient(135deg, #3b82f6, #6366f1);
            color: #fff;
            font-family: 'Sora', sans-serif;
            font-size: 1rem;
            font-weight: 700;
            cursor: pointer;
            letter-spacing: 0.3px;
            box-shadow: 0 8px 24px rgba(99,102,241,0.35);
            transition: transform 0.18s, box-shadow 0.18s;
          }

          .pay-btn-return:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 32px rgba(99,102,241,0.45);
          }

          .pay-btn-return:active { transform: translateY(0); }

          /* Mobile tweaks */
          @media (max-width: 480px) {
            .pay-success-card { padding: 2.25rem 1.5rem; border-radius: 22px; }
            .pay-success-title { font-size: 1.6rem; }
          }
        `}</style>

        <div className="pay-success-wrapper">
          <div className="pay-success-card">

            <div className="pay-success-icon">
              <CheckCircle2 size={46} color="#10b981" strokeWidth={2.2} />
            </div>

            <h1 className="pay-success-title">Payment Successful!</h1>
            <p className="pay-success-name">Thank you, {finalOrder.customer.fullName}!</p>
            <p className="pay-success-sub">Your premium gear is being prepared for shipment.</p>

            <div className="pay-whatsapp-badge">
              <MessageCircle size={17} />
              Order details sent to WhatsApp (+{process.env.REACT_APP_WHATSAPP_NUMBER})
            </div>

            <div className="pay-order-summary">
              <div className="pay-order-row">
                <span className="pay-order-label">Order ID</span>
                <span className="pay-order-value">{finalOrder.orderId}</span>
              </div>
              <div className="pay-order-row">
                <span className="pay-order-label">Total Paid</span>
                <span className="pay-order-amount">₹{finalOrder.totalAmount.toFixed(2)}</span>
              </div>
            </div>

            <button className="pay-btn-return" onClick={() => navigate('/')}>
              Return to Shop
            </button>

          </div>
        </div>
      </>
    );
  }

  /* ─── Payment Screen ─── */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');

        .pay-wrapper {
          font-family: 'Sora', sans-serif;
          min-height: 100vh;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          box-sizing: border-box;
        }

        .pay-card {
          width: 100%;
          max-width: 860px;
          background: var(--glass-panel, rgba(255,255,255,0.07));
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.13);
          border-left: 4px solid var(--primary, #3b82f6);
          border-radius: 28px;
          padding: 2.75rem 3rem;
          box-shadow: 0 24px 64px rgba(0,0,0,0.22);
          animation: slideUp 0.5s cubic-bezier(0.22,1,0.36,1) both;
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .pay-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 2.5rem;
        }

        .pay-header h2 {
          font-size: 1.55rem;
          font-weight: 800;
          margin: 0;
          background: linear-gradient(135deg, #60a5fa, #a78bfa);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          letter-spacing: -0.4px;
        }

        /* Two-column grid on desktop, stacked on mobile */
        .pay-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2.5rem;
          align-items: start;
        }

        /* QR column */
        .pay-qr-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.25rem;
        }

        .pay-qr-frame {
          background: #fff;
          padding: 1rem;
          border-radius: 20px;
          box-shadow: 0 16px 48px rgba(0,0,0,0.4);
          display: inline-flex;
        }

        .pay-qr-frame img {
          display: block;
          border-radius: 10px;
          width: 200px;
          height: 200px;
        }

        .pay-amount-pill {
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(0, 0, 0, 0.05);
          color: #111;
          padding: 1rem 1.5rem;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
          text-align: center;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
        }

        .pay-amount-pill strong {
          color: #000;
          font-size: 1.5rem;
          font-weight: 800;
          display: block;
        }

        /* Right input column */
        .pay-input-col {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .pay-confirm-box {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 18px;
          padding: 1.75rem;
        }

        .pay-confirm-box h3 {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          font-size: 1.05rem;
          font-weight: 700;
          margin: 0 0 0.6rem;
          color: var(--text-main, #f1f5f9);
        }

        .pay-confirm-box p {
          font-size: 0.875rem;
          color: var(--text-muted, #94a3b8);
          line-height: 1.6;
          margin: 0 0 1.5rem;
        }

        .pay-input-field {
          width: 100%;
          box-sizing: border-box;
          padding: 0.9rem 1.1rem;
          border-radius: 12px;
          border: 1.5px solid rgba(255,255,255,0.12);
          background: rgba(255,255,255,0.06);
          color: var(--text-main, #f1f5f9);
          font-family: 'Sora', sans-serif;
          font-size: 1rem;
          font-weight: 500;
          letter-spacing: 1.5px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .pay-input-field::placeholder {
          color: rgba(148,163,184,0.6);
          letter-spacing: 0.5px;
          font-size: 0.88rem;
        }

        .pay-input-field:focus {
          border-color: rgba(99,102,241,0.6);
          box-shadow: 0 0 0 4px rgba(99,102,241,0.12);
        }

        .pay-error {
          display: block;
          font-size: 0.8rem;
          color: #f87171;
          margin-top: 0.5rem;
          font-weight: 500;
        }

        .pay-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 1.1rem 1.5rem;
          border-radius: 14px;
          border: none;
          background: linear-gradient(135deg, #3b82f6, #6366f1);
          color: #fff;
          font-family: 'Sora', sans-serif;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          letter-spacing: 0.3px;
          box-shadow: 0 8px 28px rgba(99,102,241,0.4);
          transition: transform 0.18s, box-shadow 0.18s;
        }

        .pay-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 36px rgba(99,102,241,0.5);
        }

        .pay-btn-direct {
          width: 100%;
          display: none;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          padding: 1.1rem;
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.2);
          background: rgba(255,255,255,0.1);
          color: #080808ff;
          font-family: 'Sora', sans-serif;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 1rem;
        }

        .pay-btn-direct:hover {
          background: rgba(255,255,255,0.2);
          transform: translateY(-2px);
        }

        .pay-btn:active, .pay-btn-direct:active { transform: translateY(0); }

        .pay-footnote {
          font-size: 0.78rem;
          color: var(--text-muted, #94a3b8);
          text-align: center;
          line-height: 1.5;
        }

        /* ── Mobile: stack vertically ── */
        @media (max-width: 640px) {
          .pay-card {
            padding: 2rem 1.5rem;
            border-radius: 22px;
            border-left-width: 3px;
          }

          .pay-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .pay-qr-col { width: 100%; }

          .pay-qr-frame img { width: 180px; height: 180px; }

          .pay-header h2 { font-size: 1.3rem; }

          .pay-btn-direct {
            display: flex; /* Show on mobile devices */
          }
        }
      `}</style>

      <div className="pay-wrapper">
        <div className="pay-card">

          <div className="pay-header">
            <ShieldCheck size={30} color="#60a5fa" strokeWidth={2.2} />
            <h2>Secure Payment</h2>
          </div>

          <div className="pay-grid">

            {/* ── QR Column ── */}
            <div className="pay-qr-col">
              <div className="pay-qr-frame">
                <img src={qrUrl} alt="UPI Payment QR Code" />
              </div>
              <div className="pay-amount-pill">
                Scan &amp; pay <strong>₹{cartTotal.toFixed(2)}</strong> via any UPI app
              </div>

              {/* One-tap Payment for mobile */}
              <button
                className="pay-btn-direct"
                onClick={() => {
                  window.location.href = upiUrl;
                }}
              >
                <MessageCircle size={20} />
                Pay via UPI App
              </button>
            </div>

            {/* ── Input Column ── */}
            <div className="pay-input-col">

              <div className="pay-confirm-box">
                <h3>
                  <QrCode size={18} color="#60a5fa" />
                  Confirm Transaction
                </h3>
                <p>
                  After completing payment on your device, enter the
                  reference / transaction number below to confirm your order.
                </p>

                <input
                  type="text"
                  className="pay-input-field"
                  placeholder="e.g. TXN9876543210"
                  value={transactionRef}
                  onChange={e => {
                    setTransactionRef(e.target.value);
                    setError('');
                  }}
                />
                {error && <span className="pay-error">{error}</span>}
              </div>

              <button className="pay-btn" onClick={handleConfirm}>
                Verify &amp; Complete Order <ChevronRight size={18} />
              </button>

              <p className="pay-footnote">
                * You will be redirected to WhatsApp to send order confirmation details.
              </p>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Payment;